import { useEffect, useState } from "react";
import { TrendingUp, IndianRupee, Receipt, ShoppingBag, AlertTriangle, Wallet, PiggyBank, Percent, Boxes, Package, Truck } from "lucide-react";
import api, { inr } from "../lib/api";

const RANGES = [{ label: "7d", days: 7 }, { label: "30d", days: 30 }, { label: "90d", days: 90 }, { label: "1y", days: 365 }];

export default function PosReports() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [stock, setStock] = useState(null);

  useEffect(() => {
    api.get("/pos/reports", { params: { days } }).then((r) => setData(r.data));
  }, [days]);

  useEffect(() => {
    api.get("/pos/stock-analysis").then((r) => setStock(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-8 text-slate-500">Loading…</div>;

  const maxDaily = Math.max(1, ...data.daily_series.map((d) => d.revenue));
  const paySum = Object.values(data.payment_split).reduce((s, x) => s + x, 0) || 1;

  const stat = (icon, label, value, sub = "") => (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-5">
      <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest">{icon} {label}</div>
      <p className="text-3xl font-semibold text-slate-100 mt-2 font-mono">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );

  return (
    <div className="p-4 sm:p-8 flex flex-col" data-testid="pos-reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-100">Reports</h1>
          <p className="text-sm text-slate-500">Sales, inventory & payment insights.</p>
        </div>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button key={r.days} onClick={() => setDays(r.days)} className={`px-3 py-1.5 text-xs rounded font-mono ${days === r.days ? "bg-amber-500 text-slate-950 font-semibold" : "bg-slate-900 border border-slate-800 text-slate-400"}`}>{r.label}</button>
          ))}
        </div>
      </div>

      {/* ---- Inventory valuation (current stock snapshot) ---- */}
      {stock && (
        <div className="order-last" data-testid="pos-stock-analysis">
          <div className="border-b border-slate-800 my-8" />
          <div className="flex items-center gap-2 mb-4">
            <Boxes size={16} className="text-amber-400" />
            <h2 className="text-sm text-slate-300 uppercase tracking-widest">Inventory valuation · current stock</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {stat(<Package size={13} />, "Stock units", stock.total_units, `${stock.total_skus} products`)}
            {stat(<Wallet size={13} />, "Value at cost", inr(stock.total_cost_value), "Capital invested (purchase price)")}
            {stat(<IndianRupee size={13} />, "Value at retail", inr(stock.total_retail_value), "If sold at listed price")}
            {stat(<PiggyBank size={13} />, "Potential profit", inr(stock.potential_profit), `${stock.margin_pct}% margin · costed items`)}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {stat(<AlertTriangle size={13} />, "Out of stock", stock.out_of_stock, "products at 0 units")}
            {stat(<AlertTriangle size={13} />, "Low stock", stock.low_stock, "at or below threshold")}
            {stat(<Truck size={13} />, "Suppliers", stock.suppliers.length, "distinct sources")}
            {stat(<Percent size={13} />, "Cost coverage", `${stock.cost_coverage_pct}%`, "products with a cost price")}
          </div>
          {stock.products_without_cost > 0 && (
            <p className="text-xs text-slate-500 mb-4" data-testid="stock-cost-hint">
              {stock.products_without_cost} product{stock.products_without_cost > 1 ? "s have" : " has"} no <span className="text-amber-400">Purchase price</span> set — their cost value &amp; profit aren't counted. Add it in Admin → Products for accurate figures.
            </p>
          )}
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            {/* Value by category */}
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 overflow-x-auto">
              <h3 className="text-sm text-slate-300 uppercase tracking-widest mb-4">Value by category</h3>
              <table className="w-full text-sm">
                <thead className="text-[11px] text-slate-500 uppercase">
                  <tr><th className="text-left py-1">Category</th><th className="text-right">Units</th><th className="text-right">Cost</th><th className="text-right">Retail</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {stock.categories.map((c) => (
                    <tr key={c.name}>
                      <td className="py-2 text-slate-200 capitalize">{c.name.replace(/-/g, " ")} <span className="text-slate-600">· {c.skus}</span></td>
                      <td className="text-right font-mono text-slate-300">{c.units}</td>
                      <td className="text-right font-mono text-slate-400">{inr(c.cost_value)}</td>
                      <td className="text-right font-mono text-amber-400">{inr(c.retail_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Stock cost by supplier */}
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 overflow-x-auto">
              <h3 className="text-sm text-slate-300 uppercase tracking-widest mb-4">Stock cost by supplier</h3>
              <table className="w-full text-sm">
                <thead className="text-[11px] text-slate-500 uppercase">
                  <tr><th className="text-left py-1">Supplier</th><th className="text-right">SKUs</th><th className="text-right">Units</th><th className="text-right">Cost value</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {stock.suppliers.map((s) => (
                    <tr key={s.name}>
                      <td className="py-2 text-slate-200">{s.name}</td>
                      <td className="text-right font-mono text-slate-400">{s.skus}</td>
                      <td className="text-right font-mono text-slate-300">{s.units}</td>
                      <td className="text-right font-mono text-emerald-400">{inr(s.cost_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <h2 className="text-sm text-slate-300 uppercase tracking-widest flex items-center gap-2 mb-4"><TrendingUp size={16} className="text-amber-400" /> Sales insights · last {data.days} days</h2>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stat(<IndianRupee size={13} />, "Revenue", inr(data.total_revenue))}
        {stat(<Receipt size={13} />, "Bills", data.total_bills)}
        {stat(<ShoppingBag size={13} />, "Items sold", data.total_items_sold)}
        {stat(<TrendingUp size={13} />, "Discount given", inr(data.total_discount))}
      </div>

      {/* Profit & margin — based on product purchase price (cost) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="pos-profit-row">
        {stat(<Wallet size={13} />, "Cost of goods", inr(data.total_cogs || 0))}
        {stat(<PiggyBank size={13} />, "Gross profit", inr(data.gross_profit || 0), "Revenue − tax − cost")}
        {stat(<Percent size={13} />, "Margin", `${data.margin_pct || 0}%`)}
        {stat(<IndianRupee size={13} />, "Tax collected", inr(data.total_tax || 0), "CGST + SGST")}
      </div>
      {(data.total_cogs || 0) === 0 && (
        <p className="text-xs text-slate-500 -mt-3 mb-6">Add a <span className="text-amber-400">Purchase price</span> to your products (Admin → Products) to see accurate cost, profit &amp; margin here.</p>
      )}

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Daily revenue bar chart (CSS only) */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-5">
          <h3 className="text-sm text-slate-300 uppercase tracking-widest mb-4">Daily revenue</h3>
          {data.daily_series.length === 0 ? (
            <p className="text-slate-600 text-center py-8 text-sm">No sales yet in this window</p>
          ) : (
            <div className="flex items-end gap-1 h-48">
              {data.daily_series.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end group" title={`${d.date}: ₹${inr(d.revenue)}`}>
                  <div className="w-full bg-amber-500 hover:bg-amber-400 rounded-t transition-all" style={{ height: `${(d.revenue / maxDaily) * 100}%` }} />
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-slate-500 mt-2 font-mono">{data.daily_series[0]?.date} → {data.daily_series[data.daily_series.length - 1]?.date}</p>
        </div>

        {/* Payment split */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-5">
          <h3 className="text-sm text-slate-300 uppercase tracking-widest mb-4">Payment mode split</h3>
          <div className="space-y-3">
            {Object.entries(data.payment_split).filter(([, v]) => v > 0).map(([mode, amount]) => (
              <div key={mode}>
                <div className="flex justify-between text-xs font-mono mb-1">
                  <span className="text-slate-400 uppercase">{mode}</span>
                  <span className="text-slate-200">{inr(amount)} ({Math.round((amount / paySum) * 100)}%)</span>
                </div>
                <div className="h-2 bg-slate-800 rounded overflow-hidden">
                  <div className="h-full bg-amber-500" style={{ width: `${(amount / paySum) * 100}%` }} />
                </div>
              </div>
            ))}
            {Object.values(data.payment_split).every((v) => v === 0) && (
              <p className="text-slate-600 text-center py-6 text-sm">No payments recorded</p>
            )}
          </div>
          {data.total_tax > 0 && (
            <p className="text-xs text-slate-500 mt-4 pt-4 border-t border-slate-800 font-mono">Tax collected (CGST+SGST): {inr(data.total_tax)}</p>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top products */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-5">
          <h3 className="text-sm text-slate-300 uppercase tracking-widest mb-4">Top selling</h3>
          {data.top_products.length === 0 ? (
            <p className="text-slate-600 text-center py-6 text-sm">No sales yet</p>
          ) : (
            <div className="space-y-2">
              {data.top_products.map((p, i) => (
                <div key={p.product_id} className="flex items-center gap-3 text-sm">
                  <span className="text-slate-600 font-mono w-6">#{i + 1}</span>
                  <p className="flex-1 min-w-0 text-slate-200 truncate">{p.name}</p>
                  <span className="text-slate-400 font-mono">{p.qty} sold</span>
                  <span className="text-amber-400 font-mono w-24 text-right">{inr(p.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Low stock alerts */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-5">
          <h3 className="text-sm text-slate-300 uppercase tracking-widest mb-4 flex items-center gap-2">
            <AlertTriangle size={14} className="text-red-400" /> Low stock alerts
          </h3>
          {data.low_stock.length === 0 ? (
            <p className="text-emerald-400 text-center py-6 text-sm">✓ All products well stocked</p>
          ) : (
            <div className="space-y-2">
              {data.low_stock.map((p) => (
                <div key={p.id} className="flex items-center gap-3 text-sm">
                  <span className="shrink-0 grid place-items-center h-7 w-7 bg-red-500/20 text-red-400 rounded font-mono text-xs">{p.stock}</span>
                  <p className="flex-1 min-w-0 text-slate-200 truncate">{p.name}</p>
                  <span className="text-xs text-slate-500 font-mono">{p.sku}</span>
                </div>
              ))}
              <p className="text-[11px] text-slate-500 pt-3 border-t border-slate-800 mt-3">Products with stock ≤ 3 units. Restock soon.</p>
            </div>
          )}
        </div>
      </div>

      {/* Dead stock — never sold in this window */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 mt-6 overflow-hidden">
        <h3 className="text-sm text-slate-300 uppercase tracking-widest mb-4 flex flex-wrap items-center gap-2">
          <AlertTriangle size={14} className="text-orange-400 shrink-0" /> Dead stock — no sales in {data.days} days
        </h3>
        {(!data.dead_stock || data.dead_stock.length === 0) ? (
          <p className="text-emerald-400 text-center py-6 text-sm">✓ Everything sold at least once</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.dead_stock.map((p) => (
              <div key={p.id} className="flex items-center gap-3 text-sm bg-slate-950 border border-slate-800 rounded px-3 py-2 min-w-0">
                <span className="shrink-0 grid place-items-center h-7 w-7 bg-orange-500/20 text-orange-400 rounded font-mono text-xs">{p.stock}</span>
                <p className="flex-1 min-w-0 text-slate-200 truncate">{p.name}</p>
                <span className="shrink-0 text-xs text-slate-500 font-mono truncate max-w-[40%]">{p.sku}</span>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-slate-500 pt-3 mt-3 border-t border-slate-800">Consider promoting, bundling or discounting these pieces.</p>
      </div>
    </div>
  );
}
